import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@heroui/react';
import clsx from 'clsx';

import { useRefArchManifest } from '~/hooks/useReferenceArchitectures';

import { RefArchCard } from './components/RefArchCard';
import { APPLICATION_ARCH_TYPES, groupRefArchsByType, type RefArchTypeGroup } from './utils';

type Tier = 'application' | 'system';

export function RefArchHome(): JSX.Element {
  const { error, isLoading, manifest } = useRefArchManifest();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL params (e.g., ?tier=system&type=data-platform)
  const initialTier = (searchParams.get('tier') as Tier) || 'application';
  const initialType = searchParams.get('type');

  const [activeTier, setActiveTier] = useState<Tier>(
    initialTier === 'system' ? 'system' : 'application',
  );

  const groups = useMemo(
    () => (manifest ? groupRefArchsByType(manifest.reference_architectures) : []),
    [manifest],
  );

  const applicationGroups = useMemo(
    () => groups.filter(g => APPLICATION_ARCH_TYPES.includes(g.type)),
    [groups],
  );
  const systemGroups = useMemo(
    () => groups.filter(g => !APPLICATION_ARCH_TYPES.includes(g.type)),
    [groups],
  );

  // All groups start collapsed, unless a type was specified in the URL
  const [expanded, setExpanded] = useState<Set<string>>(
    initialType ? new Set([initialType]) : new Set(),
  );

  // Clear URL params after applying them so they don't stick on tab switches (once on mount, StrictMode-safe)
  const paramsCleared = useRef(false);
  useEffect(() => {
    if (!paramsCleared.current && (searchParams.has('tier') || searchParams.has('type'))) {
      paramsCleared.current = true;
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggleGroup = useCallback((type: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center text-error">
        Failed to load reference architectures. Please try again later.
      </div>
    );
  }

  const totalArchitectures = groups.reduce((sum, g) => sum + g.entries.length, 0);
  const activeGroups = activeTier === 'application' ? applicationGroups : systemGroups;

  const renderGroup = (group: RefArchTypeGroup): JSX.Element => {
    const isOpen = expanded.has(group.type);
    return (
      <section key={group.type}>
        <button
          type="button"
          onClick={() => toggleGroup(group.type)}
          className="flex items-center gap-2 w-full px-4 py-3 rounded-lg bg-surface-elevated hover:bg-surface-elevated/80 transition-colors cursor-pointer"
        >
          <ChevronDownIcon
            className={clsx('h-4 w-4 text-muted transition-transform flex-shrink-0', {
              '-rotate-90': !isOpen,
            })}
          />
          <h2 className="text-base font-semibold text-foreground">{group.label}</h2>
          <span className="text-xs text-muted">({group.entries.length})</span>
        </button>
        {isOpen && (
          <div className="mt-3 ml-2 grid grid-cols-1 md:grid-cols-3 gap-4">
            {group.entries.map(ra => (
              <RefArchCard key={ra.ref_arch_id} refArch={ra} />
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Reference Architectures</h1>
        <p className="text-sm text-muted mt-1">
          {totalArchitectures} system blueprints that map to design patterns
        </p>
      </div>

      {/* Tier tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-default">
        <button
          type="button"
          onClick={() => setActiveTier('application')}
          className={clsx(
            'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTier === 'application'
              ? 'text-primary border-primary'
              : 'text-muted border-transparent hover:text-foreground hover:border-default',
          )}
        >
          Application
          <span className="ml-1.5 text-xs opacity-60">
            ({applicationGroups.reduce((s, g) => s + g.entries.length, 0)})
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTier('system')}
          className={clsx(
            'px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
            activeTier === 'system'
              ? 'text-primary border-primary'
              : 'text-muted border-transparent hover:text-foreground hover:border-default',
          )}
        >
          System
          <span className="ml-1.5 text-xs opacity-60">
            ({systemGroups.reduce((s, g) => s + g.entries.length, 0)})
          </span>
        </button>
      </div>

      {/* Active tier content */}
      <div className="space-y-3">{activeGroups.map(renderGroup)}</div>
    </div>
  );
}
